import { ApolloError } from "@apollo/client/errors";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactsIcon from "@mui/icons-material/Contacts";
import DeleteIcon from "@mui/icons-material/Delete";
import FactoryIcon from "@mui/icons-material/Factory";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HelpIcon from "@mui/icons-material/Help";
import MapIcon from "@mui/icons-material/Map";
import SettingsIcon from "@mui/icons-material/Settings";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { type FeatureFlagMap, FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useProducts } from "hooks/useProducts";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import { type NavItem } from "../../components/LeftNavV2/LeftNavDrawerV2";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useStaffNavItemsV2 } from "../Staff/staffNavItemsV2";

export type CustomerNavBuilderOptions = {
  flags: Partial<FeatureFlagMap> | undefined;
  hasPermissions: (permissions: Permission[]) => boolean;
  isPartner: boolean;
  isStaff: boolean;
  staffNavItems: NavItem[];
  tenantId: string | undefined;
  hasProducts: boolean;
  productCatalogLabel: string;
};

function asSection(text: string): NavItem {
  return {
    kind: "Section",
    text,
  };
}

export function buildCustomerNavItemsV2({
  flags,
  hasPermissions,
  isPartner,
  isStaff,
  staffNavItems,
  tenantId,
  hasProducts,
  productCatalogLabel,
}: CustomerNavBuilderOptions): NavItem[] {
  if (!tenantId) {
    if (isStaff) {
      return staffNavItems;
    }

    return [];
  }

  const navItems: NavItem[] = [];

  const programItems: NavItem[] = [
    {
      kind: "Group",
      text: "Chemicals",
      Icon: WarehouseIcon,
      defaultPath: `o/${tenantId}/chemicals/overview`,
      children: [
        {
          kind: "GroupChild",
          text: "Overview",
          path: `o/${tenantId}/chemicals/overview`,
        },
        ...(hasPermissions([Permission.RouteEpcraReports])
          ? [
              {
                kind: "GroupChild" as const,
                text: "Reports",
                path: `o/${tenantId}/chemicals/reports`,
              },
            ]
          : []),
        {
          kind: "GroupChild",
          text: "Inventory",
          path: `o/${tenantId}/chemicals/inventory`,
        },
        ...(hasPermissions([Permission.RouteEpcraProducts])
          ? [
              {
                kind: "GroupChild" as const,
                text: "Chem Catalog",
                path: `o/${tenantId}/chemicals/catalog/chemicals`,
              },
            ]
          : []),
        ...(hasProducts && hasPermissions([Permission.RouteEpcraProducts])
          ? [
              {
                kind: "GroupChild" as const,
                text: productCatalogLabel,
                path: `o/${tenantId}/chemicals/products`,
              },
            ]
          : []),
        ...(hasPermissions([Permission.RouteContacts])
          ? [
              {
                kind: "GroupChild" as const,
                text: "Chem Contacts",
                path: `o/${tenantId}/chemicals/contacts`,
              },
            ]
          : []),
        ...(hasPermissions([Permission.RouteDocuments])
          ? [
              {
                kind: "GroupChild" as const,
                text: "Chem Documents",
                path: `o/${tenantId}/chemicals/documents`,
              },
            ]
          : []),
      ],
    },
  ];

  const shouldShowWaste = Boolean(flags?.[FeatureFlagKey.WasteModule]) || isStaff;
  if (shouldShowWaste) {
    programItems.push({
      kind: "Group",
      text: "Waste",
      Icon: DeleteIcon,
      defaultPath: `o/${tenantId}/waste/overview`,
      children: [
        {
          kind: "GroupChild",
          text: "Overview",
          path: `o/${tenantId}/waste/overview`,
        },
        {
          kind: "GroupChild",
          text: "Waste Facilities",
          path: `o/${tenantId}/waste/facilities`,
        },
        {
          kind: "GroupChild",
          text: "Manifests",
          path: `o/${tenantId}/waste/manifests`,
        },
        {
          kind: "GroupChild",
          text: "Line Items",
          path: `o/${tenantId}/waste/manifest-line-items`,
        },
        ...(flags?.[FeatureFlagKey.WasteSubmissions]
          ? [
              {
                kind: "GroupChild" as const,
                text: "Submissions",
                path: `o/${tenantId}/waste/submissions`,
              },
            ]
          : []),
      ],
    });
  }

  if (programItems.length > 0) {
    navItems.push(asSection("Programs"), ...programItems);
  }

  const toolkitItems: NavItem[] = [
    ...(flags?.[FeatureFlagKey.TasksModule]
      ? [
          {
            kind: "Standard" as const,
            text: "Tasks",
            Icon: AssignmentTurnedInIcon,
            path: `o/${tenantId}/tasks`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteInsights])
      ? [
          {
            kind: "Standard" as const,
            text: "Insights",
            Icon: MapIcon,
            path: `o/${tenantId}/insights`,
          },
        ]
      : []),
  ];

  if (toolkitItems.length > 0) {
    navItems.push(asSection("Toolkit"), ...toolkitItems);
  }

  const catalogItems: NavItem[] = [
    ...(hasPermissions([Permission.RouteFacilities])
      ? [
          {
            kind: "Standard" as const,
            text: "Facilities",
            Icon: FactoryIcon,
            path: `o/${tenantId}/facilities`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteContacts])
      ? [
          {
            kind: "Standard" as const,
            text: "Contacts",
            Icon: ContactsIcon,
            path: `o/${tenantId}/contacts`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteDocuments])
      ? [
          {
            kind: "Standard" as const,
            text: "Documents",
            Icon: ArticleIcon,
            path: `o/${tenantId}/documents`,
          },
        ]
      : []),
    ...(hasPermissions([Permission.RouteEpcraCredentials])
      ? [
          {
            kind: "Standard" as const,
            text: "Credentials",
            Icon: BadgeIcon,
            path: `o/${tenantId}/credentials`,
          },
        ]
      : []),
    ...(isPartner
      ? [
          {
            kind: "Standard" as const,
            text: "Organizations",
            Icon: GroupsRoundedIcon,
            path: `o/${tenantId}/organizations`,
          },
        ]
      : []),
  ];

  if (catalogItems.length > 0) {
    navItems.push(asSection("Catalogs"), ...catalogItems);
  }

  const setupItems: NavItem[] = [
    ...(hasPermissions([Permission.WriteAllUserTenant])
      ? [
          {
            kind: "Standard" as const,
            text: "Account",
            Icon: SettingsIcon,
            path: `o/${tenantId}/account/users`,
            isDisabled: false,
          },
        ]
      : []),
    {
      kind: "Standard",
      text: "Help",
      Icon: HelpIcon,
      path: "http://support.encamp.com",
      isDisabled: false,
      isExternal: true,
    },
  ];

  if (setupItems.length > 0) {
    navItems.push(asSection("Setup"), ...setupItems);
  }

  if (isStaff) {
    navItems.push(...staffNavItems);
  }

  return navItems;
}

export function useCustomerNavItemsV2(): {
  error: ApolloError | undefined;
  navItems: NavItem[];
} {
  const { isStaff, isPartner } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  const { error, tenant, isLKQ } = useTenant();
  const { hasProducts } = useProducts();
  const { featureFlags: flags } = useFeatureFlags();
  const staffNavItems = useStaffNavItemsV2();

  const navItems = useMemo<NavItem[]>(() => {
    return buildCustomerNavItemsV2({
      flags,
      hasPermissions,
      isPartner,
      isStaff,
      staffNavItems,
      tenantId: tenant?.id,
      hasProducts: Boolean(hasProducts),
      productCatalogLabel: isLKQ ? "Products / Equipment Catalog" : "Product Catalog",
    });
  }, [flags, hasPermissions, hasProducts, isLKQ, isPartner, isStaff, staffNavItems, tenant?.id]);

  return { error, navItems };
}
